import { useState, useEffect, useCallback } from 'react';
import {
  Plus,
  Search,
  Edit2,
  Trash2,
  X,
  Check,
  ToggleLeft,
  ToggleRight,
  Type,
  Hash,
  Mail,
  Phone,
  Link,
  Calendar,
  Clock,
  List,
  CheckSquare,
  Circle,
  AlignLeft,
  Lock,
  Filter,
} from 'lucide-react';
import { Button, Badge, Spinner, Modal, useToast } from '@centraoneplus/ui';
import {
  fieldDefinitionsService,
  CreateFieldDefinitionDto,
  UpdateFieldDefinitionDto,
} from '@services/field-definitions.service';
import type { FieldDefinition, FieldType, FieldOption } from '@centraoneplus/shared-types';
import { ThemedSelect } from '@components/common/ThemedSelect';

// Field type configurations
const FIELD_TYPES: {
  value: FieldType;
  label: string;
  description: string;
  icon: React.ElementType;
}[] = [
  { value: 'text', label: 'Text', description: 'Single line text', icon: Type },
  { value: 'textarea', label: 'Text Area', description: 'Multi-line text', icon: AlignLeft },
  { value: 'number', label: 'Number', description: 'Numeric input', icon: Hash },
  { value: 'email', label: 'Email', description: 'Email address', icon: Mail },
  { value: 'phone', label: 'Phone', description: 'Phone number', icon: Phone },
  { value: 'url', label: 'URL', description: 'Web address', icon: Link },
  { value: 'date', label: 'Date', description: 'Date picker', icon: Calendar },
  { value: 'datetime', label: 'Date & Time', description: 'Date and time picker', icon: Clock },
  { value: 'select', label: 'Dropdown', description: 'Single selection', icon: List },
  { value: 'multiselect', label: 'Multi-select', description: 'Multiple selections', icon: CheckSquare },
  { value: 'checkbox', label: 'Checkbox', description: 'Yes/No toggle', icon: Check },
  { value: 'radio', label: 'Radio', description: 'Single choice from options', icon: Circle },
];

function getFieldTypeIcon(fieldType: FieldType) {
  const type = FIELD_TYPES.find((t) => t.value === fieldType);
  return type?.icon || Type;
}

function getFieldTypeLabel(fieldType: FieldType) {
  const type = FIELD_TYPES.find((t) => t.value === fieldType);
  return type?.label || fieldType;
}

// Option editor component
function OptionEditor({
  options,
  onChange,
}: {
  options: FieldOption[];
  onChange: (options: FieldOption[]) => void;
}) {
  const addOption = () => {
    onChange([...options, { label: '', value: '' }]);
  };

  const updateOption = (index: number, field: 'label' | 'value' | 'color', value: string) => {
    const updated = [...options];
    updated[index] = { ...updated[index], [field]: value };
    // Auto-generate value from label if value is empty
    if (field === 'label' && !updated[index].value) {
      updated[index].value = value
        .toLowerCase()
        .replace(/\s+/g, '_')
        .replace(/[^a-z0-9_]/g, '');
    }
    onChange(updated);
  };

  const removeOption = (index: number) => {
    onChange(options.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
        Options
      </label>
      <div className="space-y-2">
        {options.map((option, index) => (
          <div key={index} className="flex items-center gap-2">
            <input
              type="text"
              value={option.label}
              onChange={(e) => updateOption(index, 'label', e.target.value)}
              placeholder="Label"
              className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800"
            />
            <input
              type="text"
              value={option.value}
              onChange={(e) => updateOption(index, 'value', e.target.value)}
              placeholder="Value"
              className="w-32 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-mono bg-white dark:bg-gray-800"
            />
            <input
              type="color"
              value={option.color || '#6b7280'}
              onChange={(e) => updateOption(index, 'color', e.target.value)}
              className="w-10 h-10 rounded cursor-pointer"
              title="Color"
            />
            <button
              type="button"
              onClick={() => removeOption(index)}
              className="p-2 text-gray-400 hover:text-red-500 transition-colors dark:text-gray-500"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={addOption}
        className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700"
      >
        <Plus className="h-4 w-4" />
        Add option
      </button>
    </div>
  );
}

// Field form component
interface FieldFormProps {
  field?: FieldDefinition;
  onSubmit: (data: CreateFieldDefinitionDto) => Promise<void>;
  onCancel: () => void;
  isLoading: boolean;
}

function FieldForm({ field, onSubmit, onCancel, isLoading }: FieldFormProps) {
  const [formData, setFormData] = useState<CreateFieldDefinitionDto>({
    name: field?.name || '',
    key: field?.key || '',
    description: field?.description || '',
    fieldType: field?.fieldType || 'text',
    options: field?.options || [],
    defaultValue: field?.defaultValue,
    validation: field?.validation || {},
    placeholder: field?.placeholder || '',
    helpText: field?.helpText || '',
    isActive: field?.isActive ?? true,
    // AI Extraction support
    keywords: (field as any)?.keywords || [],
    examples: (field as any)?.examples || [],
  });

  const [keywordsInput, setKeywordsInput] = useState(((field as any)?.keywords || []).join(', '));
  const [examplesInput, setExamplesInput] = useState(((field as any)?.examples || []).join(', '));

  const [errors, setErrors] = useState<Record<string, string>>({});

  const needsOptions = ['select', 'multiselect', 'radio'].includes(formData.fieldType);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    if (needsOptions && (!formData.options || formData.options.length === 0)) {
      newErrors.options = 'At least one option is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    // Parse comma-separated keywords and examples
    const dataWithKeywords = {
      ...formData,
      keywords: keywordsInput.split(',').map((k: string) => k.trim()).filter(Boolean),
      examples: examplesInput.split(',').map((e: string) => e.trim()).filter(Boolean),
    };
    await onSubmit(dataWithKeywords);
  };

  const handleNameChange = (name: string) => {
    setFormData({
      ...formData,
      name,
      key: field ? formData.key : fieldDefinitionsService.generateKey(name),
    });
  };

  const Icon = getFieldTypeIcon(formData.fieldType);

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Preview */}
      <div className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
        <div className="w-10 h-10 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center text-primary-600">
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <p className="font-medium text-gray-900 dark:text-gray-100">
            {formData.name || 'Field Name'}
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {getFieldTypeLabel(formData.fieldType)} • {formData.key || 'field_key'}
          </p>
        </div>
      </div>

      {/* Basic Info */}
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Field Name *
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => handleNameChange(e.target.value)}
            placeholder="e.g., Product Category"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800"
          />
          {errors.name && <p className="mt-1 text-xs text-red-500">{errors.name}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Field Key *
          </label>
          <input
            type="text"
            value={formData.key}
            onChange={(e) => setFormData({ ...formData, key: e.target.value })}
            placeholder="product_category"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-mono bg-white dark:bg-gray-800"
            disabled={!!field}
          />
          {field && <p className="text-xs text-gray-500 mt-1 dark:text-gray-400">Key cannot be changed</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Field Type *
          </label>
          <ThemedSelect
            value={formData.fieldType}
            onChange={(e) =>
              setFormData({ ...formData, fieldType: e.target.value as FieldType })
            }
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800"
            disabled={!!field}
          >
            {FIELD_TYPES.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </ThemedSelect>
          {field && <p className="text-xs text-gray-500 mt-1 dark:text-gray-400">Type cannot be changed</p>}
        </div>

        <div className="col-span-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Description
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Brief description of this field..."
            rows={2}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Placeholder
          </label>
          <input
            type="text"
            value={formData.placeholder}
            onChange={(e) => setFormData({ ...formData, placeholder: e.target.value })}
            placeholder="Enter placeholder text..."
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Help Text
          </label>
          <input
            type="text"
            value={formData.helpText}
            onChange={(e) => setFormData({ ...formData, helpText: e.target.value })}
            placeholder="Help text shown below field..."
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800"
          />
        </div>
      </div>

      {/* Options for select/multiselect/radio */}
      {needsOptions && (
        <div>
          <OptionEditor
            options={formData.options || []}
            onChange={(options) => setFormData({ ...formData, options })}
          />
          {errors.options && <p className="mt-1 text-xs text-red-500">{errors.options}</p>}
        </div>
      )}

      {/* Validation */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Validation
        </label>
        <div className="grid grid-cols-2 gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.validation?.required || false}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  validation: { ...formData.validation, required: e.target.checked },
                })
              }
              className="h-4 w-4 text-primary-600 border-gray-300 rounded dark:border-gray-700"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">Required</span>
          </label>

          {['text', 'textarea'].includes(formData.fieldType) && (
            <>
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">
                  Min Length:
                </label>
                <input
                  type="number"
                  min="0"
                  value={formData.validation?.minLength || ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      validation: {
                        ...formData.validation,
                        minLength: e.target.value ? parseInt(e.target.value) : undefined,
                      },
                    })
                  }
                  className="w-20 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800"
                />
              </div>
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">
                  Max Length:
                </label>
                <input
                  type="number"
                  min="0"
                  value={formData.validation?.maxLength || ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      validation: {
                        ...formData.validation,
                        maxLength: e.target.value ? parseInt(e.target.value) : undefined,
                      },
                    })
                  }
                  className="w-20 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800"
                />
              </div>
            </>
          )}

          {formData.fieldType === 'number' && (
            <>
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">
                  Min:
                </label>
                <input
                  type="number"
                  value={formData.validation?.min ?? ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      validation: {
                        ...formData.validation,
                        min: e.target.value ? parseInt(e.target.value) : undefined,
                      },
                    })
                  }
                  className="w-20 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800"
                />
              </div>
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">
                  Max:
                </label>
                <input
                  type="number"
                  value={formData.validation?.max ?? ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      validation: {
                        ...formData.validation,
                        max: e.target.value ? parseInt(e.target.value) : undefined,
                      },
                    })
                  }
                  className="w-20 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800"
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* AI Extraction Support */}
      <div className="space-y-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-blue-800 dark:text-blue-300">
            AI Extraction Settings
          </span>
          <span className="text-xs text-blue-600 dark:text-blue-400">
            (Used by LLM to extract this field from emails)
          </span>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Keywords (comma-separated)
          </label>
          <input
            type="text"
            value={keywordsInput}
            onChange={(e) => setKeywordsInput(e.target.value)}
            placeholder="e.g., MPAN:, Meter Point, Supply Number"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800"
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            AI looks for these keywords to identify and extract this field from text
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Example Values (comma-separated)
          </label>
          <input
            type="text"
            value={examplesInput}
            onChange={(e) => setExamplesInput(e.target.value)}
            placeholder="e.g., 1300053765675, 1234567890123"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800"
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Example values help AI understand the expected format
          </p>
        </div>
      </div>

      {/* Active toggle */}
      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={formData.isActive}
          onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
          className="h-4 w-4 text-primary-600 border-gray-300 rounded dark:border-gray-700"
        />
        <span className="text-sm text-gray-700 dark:text-gray-300">Active</span>
      </label>

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading}>
          Cancel
        </Button>
        <Button type="submit" disabled={isLoading}>
          {isLoading ? 'Saving...' : field ? 'Update' : 'Create'}
        </Button>
      </div>
    </form>
  );
}

// Main component
export function FieldDefinitionsSettings() {
  const toast = useToast();
  const [fields, setFields] = useState<FieldDefinition[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<FieldType | 'all'>('all');
  const [showSystemFields, setShowSystemFields] = useState(true);

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingField, setEditingField] = useState<FieldDefinition | null>(null);
  const [deletingField, setDeletingField] = useState<FieldDefinition | null>(null);

  const loadFields = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await fieldDefinitionsService.getFieldDefinitions();
      setFields(data);
    } catch (error: any) {
      toast.error('Error', error.message || 'Failed to load field definitions');
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadFields();
  }, [loadFields]);

  const handleCreate = async (data: CreateFieldDefinitionDto) => {
    try {
      setIsSaving(true);
      await fieldDefinitionsService.createFieldDefinition(data);
      toast.success('Success', 'Field created successfully');
      setShowCreateModal(false);
      loadFields();
    } catch (error: any) {
      toast.error('Error', error.message || 'Failed to create field');
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdate = async (data: UpdateFieldDefinitionDto) => {
    if (!editingField) return;
    try {
      setIsSaving(true);
      await fieldDefinitionsService.updateFieldDefinition(editingField.id, data);
      toast.success('Success', 'Field updated successfully');
      setEditingField(null);
      loadFields();
    } catch (error: any) {
      toast.error('Error', error.message || 'Failed to update field');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingField) return;
    try {
      setIsSaving(true);
      await fieldDefinitionsService.deleteFieldDefinition(deletingField.id);
      toast.success('Success', 'Field deleted successfully');
      setDeletingField(null);
      loadFields();
    } catch (error: any) {
      toast.error('Error', error.message || 'Failed to delete field');
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleActive = async (field: FieldDefinition) => {
    if (field.isSystem) {
      toast.error('Error', 'Cannot deactivate system fields');
      return;
    }
    try {
      await fieldDefinitionsService.updateFieldDefinition(field.id, {
        isActive: !field.isActive,
      });
      toast.success('Success', `Field ${field.isActive ? 'deactivated' : 'activated'}`);
      loadFields();
    } catch (error: any) {
      toast.error('Error', error.message || 'Failed to update field');
    }
  };

  // Filter fields
  const filteredFields = fields.filter((field) => {
    const matchesSearch =
      field.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      field.key.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = filterType === 'all' || field.fieldType === filterType;
    const matchesSystem = showSystemFields || !field.isSystem;
    return matchesSearch && matchesType && matchesSystem;
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Field Definitions
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Create reusable custom fields that can be added to ticket types
          </p>
        </div>
        <Button onClick={() => setShowCreateModal(true)}>
          <Plus className="h-4 w-4 mr-2" />
          New Field
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-500" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search fields..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800"
          />
        </div>

        <ThemedSelect
          value={filterType}
          onChange={(e) => setFilterType(e.target.value as FieldType | 'all')}
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800"
        >
          <option value="all">All Types</option>
          {FIELD_TYPES.map((type) => (
            <option key={type.value} value={type.value}>
              {type.label}
            </option>
          ))}
        </ThemedSelect>

        <label className="flex flex-shrink-0 items-center gap-2 whitespace-nowrap pr-1 cursor-pointer text-sm text-gray-600 dark:text-gray-400">
          <input
            type="checkbox"
            checked={showSystemFields}
            onChange={(e) => setShowSystemFields(e.target.checked)}
            className="h-4 w-4 text-primary-600 border-gray-300 rounded dark:border-gray-700"
          />
          Show system fields
        </label>
      </div>

      {/* Fields List */}
      {filteredFields.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <Type className="h-12 w-12 text-gray-400 mx-auto mb-4 dark:text-gray-500" />
          <p className="text-gray-500 dark:text-gray-400">
            {searchQuery || filterType !== 'all'
              ? 'No fields found matching your filters'
              : 'No custom fields yet'}
          </p>
          {!searchQuery && filterType === 'all' && (
            <Button onClick={() => setShowCreateModal(true)} className="mt-4">
              Create your first field
            </Button>
          )}
        </div>
      ) : (
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-x-auto">
          {/* table-fixed sizes columns from this header row: the four short
              columns are pinned and Field takes the rest, never less than 192px. */}
          <table className="w-full min-w-[680px] table-fixed">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">
                  Field
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400 w-[108px]">
                  Type
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400 w-[152px]">
                  Key
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400 w-[92px]">
                  Status
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400 w-[136px] whitespace-nowrap">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
              {filteredFields.map((field) => {
                const Icon = getFieldTypeIcon(field.fieldType);
                return (
                  <tr key={field.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                    <td className="px-4 py-3 min-w-0">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-8 h-8 rounded bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-gray-500 dark:text-gray-400 flex-shrink-0">
                          <Icon className="w-4 h-4" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="font-medium text-gray-900 dark:text-gray-100 truncate" title={field.name}>
                              {field.name}
                            </span>
                            {field.isSystem && (
                              <Lock className="h-3 w-3 text-gray-400 dark:text-gray-500 flex-shrink-0" aria-label="System field" />
                            )}
                          </div>
                          {field.description && (
                            <p className="text-xs text-gray-500 truncate dark:text-gray-400" title={field.description}>
                              {field.description}
                            </p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant="secondary" size="sm">
                        {getFieldTypeLabel(field.fieldType)}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 min-w-0">
                      <code className="text-sm text-gray-600 dark:text-gray-400 font-mono truncate block" title={field.key}>
                        {field.key}
                      </code>
                    </td>
                    <td className="px-4 py-3">
                      {field.isActive ? (
                        <Badge variant="success" size="sm">
                          Active
                        </Badge>
                      ) : (
                        <Badge variant="secondary" size="sm">
                          Inactive
                        </Badge>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => handleToggleActive(field)}
                          disabled={field.isSystem}
                          className={`p-2 rounded-lg transition-colors ${
                            field.isSystem
                              ? 'text-gray-300 cursor-not-allowed'
                              : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-gray-500'
                          }`}
                          title={field.isSystem ? 'System field' : field.isActive ? 'Deactivate' : 'Activate'}
                        >
                          {field.isActive ? (
                            <ToggleRight className="h-4 w-4 text-green-500" />
                          ) : (
                            <ToggleLeft className="h-4 w-4" />
                          )}
                        </button>

                        <button
                          onClick={() => setEditingField(field)}
                          className="p-2 rounded-lg text-gray-400 hover:text-primary-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors dark:text-gray-500"
                          title="Edit"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>

                        <button
                          onClick={() => setDeletingField(field)}
                          disabled={field.isSystem}
                          className={`p-2 rounded-lg transition-colors ${
                            field.isSystem
                              ? 'text-gray-300 cursor-not-allowed'
                              : 'text-gray-400 hover:text-red-500 hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-gray-500'
                          }`}
                          title={field.isSystem ? 'Cannot delete system field' : 'Delete'}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Create Field Definition"
        size="lg"
      >
        <FieldForm
          onSubmit={handleCreate}
          onCancel={() => setShowCreateModal(false)}
          isLoading={isSaving}
        />
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={!!editingField}
        onClose={() => setEditingField(null)}
        title="Edit Field Definition"
        size="lg"
      >
        {editingField && (
          <FieldForm
            field={editingField}
            onSubmit={handleUpdate}
            onCancel={() => setEditingField(null)}
            isLoading={isSaving}
          />
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={!!deletingField}
        onClose={() => setDeletingField(null)}
        title="Delete Field Definition"
      >
        <div className="space-y-4">
          <p className="text-gray-600 dark:text-gray-400">
            Are you sure you want to delete <strong>{deletingField?.name}</strong>?
          </p>
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
            <p className="text-sm text-yellow-800 dark:text-yellow-200">
              Warning: You cannot delete a field that is used by ticket types. Remove it from all
              ticket types first.
            </p>
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <Button variant="outline" onClick={() => setDeletingField(null)} disabled={isSaving}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDelete} disabled={isSaving}>
              {isSaving ? 'Deleting...' : 'Delete'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
